import React, { createContext, useState, useCallback, useContext, useMemo, useEffect, useRef } from 'react'; // Added useRef
import api from '../../../../config/axios';
import { formatErrorMessage } from '../../../../utils/apiUtils';
// Assuming you have a logger utility
import { logger } from '../../../../utils/logger';


export const TableContext = createContext(undefined);

export const useTableContext = () => {
  const context = useContext(TableContext);
  if (context === undefined) {
    throw new Error('useTableContext must be used within a TableProvider');
  }
  return context;
};

export function TableContextProvider({ 
  children, 
  tableName, 
  primaryKey, 
  apiEndpoint = '/crud',
  initialFilters: propInitialFilters = {},
  autoSearchOnInitialFilters: propAutoSearchOnInitialFilters = false,
  autoSearchOnLoad = true, // <-- AGGIUNGI QUESTA RIGA
  initialPagination = { limit: 25, offset: 0 },
  initialSortBy = [],
  defaultSortField,
  defaultSortDirection = 'asc',
  pinnedTopFilters = null
}) {
  // --- State Declarations ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // --- Processing States for CRUD Operations ---
  const [isProcessingCreate, setIsProcessingCreate] = useState(false);
  const [isProcessingUpdate, setIsProcessingUpdate] = useState(false);
  const [isProcessingDelete, setIsProcessingDelete] = useState(false);

  // Internal states, initialized by props
  const [filters, setFilters] = useState(propInitialFilters);
  const [pagination, setPagination] = useState(initialPagination);
  
  // NEW: Initialize sortBy with defaultSortField and direction if provided and no initial sort is specified
  const [sortBy, setSortBy] = useState(() => {
    // If initialSortBy is provided, use it
    if (initialSortBy && initialSortBy.length > 0) {
      return initialSortBy;
    }
    // If defaultSortField is provided and no initial sort, create default sort with direction
    if (defaultSortField) {
      return [{ 
        id: defaultSortField, 
        desc: defaultSortDirection === 'desc' 
      }];
    }
    // Otherwise, no initial sort
    return [];
  });

  const [selectedRow, setSelectedRow] = useState(null);
  const [conflictData, setConflictData] = useState(null);
  const [lookupOptions, setLookupOptions] = useState({});
  const [tableInfo, setTableInfo] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    lastSearchTime: null,
  });

  // Ref to store the previous propInitialFilters
  const prevPropInitialFiltersRef = useRef();
  
  // AGGIUNTO: Ref per tracciare se la ricerca iniziale automatica è già stata eseguita
  const initialAutoSearchExecutedRef = useRef(false);

  // --- State per callback stabili ---
  const stableCallbacksRef = useRef({});

  // --- Effect to synchronize propInitialFilters with the internal filters state ---
  useEffect(() => {
    // Only update the internal 'filters' state if 'propInitialFilters' itself has changed
    // since the last render. This prevents overwriting user-applied filters if 'propInitialFilters'
    // is stable but the internal 'filters' state changes.
    if (JSON.stringify(propInitialFilters) !== JSON.stringify(prevPropInitialFiltersRef.current)) {
      // logger.info(`[TableContext-${tableName}] propInitialFilters changed. Updating internal 'filters' state to:`, propInitialFilters);
      setFilters(propInitialFilters);
      // Reset pagination when initial/external filters change, as the dataset is likely new.
      setPagination(prev => ({ ...prev, offset: 0 }));
      
      // ✅ SOLUZIONE: Reset del flag di ricerca automatica quando cambiano i filtri iniziali
      initialAutoSearchExecutedRef.current = false;
    }

    // Update the ref *after* the comparison for the next render's comparison.
    prevPropInitialFiltersRef.current = propInitialFilters;
  }, [propInitialFilters, tableName]); // This effect ONLY depends on propInitialFilters and tableName.

  // --- API Functions ---
  const fetchOrSearch = useCallback(async (fetchOptions = {}) => {

    
    setLoading(true);
    setError(null);

    const intendedFilters = fetchOptions.filters;
    const intendedLimit = fetchOptions.limit !== undefined ? fetchOptions.limit : pagination.limit;
    const intendedOffset = fetchOptions.offset !== undefined ? fetchOptions.offset : pagination.offset;
    const intendedSortBy = fetchOptions.sortBy !== undefined ? fetchOptions.sortBy : sortBy;



    let orderBy = null;
    let orderDirection = null;
    if (intendedSortBy && intendedSortBy.length > 0) {
      // ✅ SUPPORTO ORDINAMENTO MULTIPLO
      // Costruisci stringa SQL con tutte le colonne di ordinamento
      const orderClauses = intendedSortBy.map(sort => 
        `\`${sort.id}\` ${sort.desc ? 'DESC' : 'ASC'}`
      );
      orderBy = orderClauses.join(', ');
      // Non serve più orderDirection separato quando abbiamo orderBy completo
    }

    const params = {
      limit: intendedLimit,
      offset: intendedOffset,
      filters: JSON.stringify(intendedFilters),
      ...(orderBy && { orderBy }),
      // ✅ RIMUOVI orderDirection perché ora è incluso in orderBy
    };



    try {
      const mainRequest = api.get(`${apiEndpoint}/${tableName}`, { params });
      const hasPinnedTopFilters = pinnedTopFilters
        && typeof pinnedTopFilters === 'object'
        && Object.keys(pinnedTopFilters).length > 0;
      const pinnedRequest = hasPinnedTopFilters
        ? api.get(`${apiEndpoint}/${tableName}`, {
            params: {
              limit: 1,
              offset: 0,
              filters: JSON.stringify(pinnedTopFilters),
            },
          }).catch((pinnedError) => {
            logger.warn(
              `[TableContext-${tableName}] Impossibile recuperare il record fissato in testa:`,
              pinnedError
            );
            return null;
          })
        : Promise.resolve(null);

      const [response, pinnedResponse] = await Promise.all([mainRequest, pinnedRequest]);
      const records = response.data?.records || response.data || [];
      const pinnedRecords = pinnedResponse?.data?.records || pinnedResponse?.data || [];
      const responsePagination = response.data?.pagination;
      const normalizedPinnedRecords = Array.isArray(pinnedRecords) ? pinnedRecords : [];
      const pinnedPrimaryKeys = new Set(
        normalizedPinnedRecords
          .map(record => record?.[primaryKey])
          .filter(value => value !== null && value !== undefined)
          .map(String)
      );
      const recordsWithPinnedTop = [
        ...normalizedPinnedRecords,
        ...records.filter(record => {
          const recordPrimaryKey = record?.[primaryKey];
          return recordPrimaryKey === null
            || recordPrimaryKey === undefined
            || !pinnedPrimaryKeys.has(String(recordPrimaryKey));
        }),
      ];

      const totalRecords = responsePagination?.totalRecords ?? records.length;
      const totalPages = responsePagination?.totalPages ?? (intendedLimit > 0 ? Math.ceil(totalRecords / intendedLimit) : 1);
      const currentPage = responsePagination?.currentPage ?? (intendedLimit > 0 ? Math.floor(intendedOffset / intendedLimit) + 1 : 1);

      setData(recordsWithPinnedTop);
      setTableInfo({ totalRecords, totalPages, currentPage, lastSearchTime: Date.now() });

      return { success: true, data: recordsWithPinnedTop };
    } catch (err) {
      const formattedError = formatErrorMessage(err);
      setError(formattedError);
      setData([]);
      setTableInfo({ totalRecords: 0, totalPages: 0, currentPage: 1, lastSearchTime: null });
      return { success: false, error: formattedError };
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint, tableName, primaryKey, pagination.limit, pagination.offset, sortBy, pinnedTopFilters]);

  // fetchRecords - versione stabile CON fallback ai filtri interni
  const fetchRecords = useCallback((options = {}) => {
    // Se non vengono passati filtri esplicitamente, usa quelli del contesto
    const filtersToUse = options.filters !== undefined ? options.filters : filters;
    return fetchOrSearch({ ...options, filters: filtersToUse });
  }, [fetchOrSearch, filters]);

  // searchRecords - Semplificato, rimosso setTimeout
  const searchRecords = useCallback((options = {}) => {

    // CORREZIONE: Se vengono passati dei filtri, usali. Altrimenti, usa i filtri di stato.
    // Questo garantisce che la ricerca usi sempre i dati più aggiornati quando forniti.
    const filtersToUse = options.filters !== undefined ? options.filters : filters;
    
    return fetchOrSearch({
      ...options,
      filters: filtersToUse, // Usa i filtri corretti
      offset: 0,
    });
  }, [fetchOrSearch, tableName, filters]); // Aggiungi 'filters' alle dipendenze

  // --- Effect for Data Fetching (VERSIONE SEMPLIFICATA) ---
  useEffect(() => {
    if (!tableName || !primaryKey) return;

    const hasInitialFilters = propInitialFilters && Object.keys(propInitialFilters).length > 0;
    
    // SOLO per auto-search iniziale, NON per filtri utente
    if (propAutoSearchOnInitialFilters && hasInitialFilters && !initialAutoSearchExecutedRef.current) {
      const timeoutId = setTimeout(async () => {
        logger.debug(`[TableContext-${tableName}] Eseguendo auto-ricerca iniziale con filtri:`, propInitialFilters);
        initialAutoSearchExecutedRef.current = true;
        
        // Directly call fetchOrSearch with the correct, up-to-date filters
        await fetchOrSearch({
          filters: propInitialFilters,
          offset: 0, // Reset to first page for new filter
        });

      }, 50); // Reduced timeout slightly

      return () => clearTimeout(timeoutId);

    } else if (!propAutoSearchOnInitialFilters && autoSearchOnLoad && !initialAutoSearchExecutedRef.current) {
      const timeoutId = setTimeout(async () => {
        logger.debug(`[TableContext-${tableName}] Eseguendo ricerca iniziale vuota (nessun filtro predefinito)`);
        initialAutoSearchExecutedRef.current = true;
        // Use fetchRecords which correctly uses the internal (empty) filter state
        await fetchRecords(); 
      }, 50); // Reduced timeout slightly

      return () => clearTimeout(timeoutId);
    }
    
    // NON fare nulla per i filtri utente - lascia che handleSearch gestisca tutto
  }, [tableName, primaryKey, JSON.stringify(propInitialFilters), propAutoSearchOnInitialFilters, autoSearchOnLoad, apiEndpoint]);


  // --- CRUD Operations ---
  const createRecord = useCallback(async (newData) => {
    setLoading(true);
    setIsProcessingCreate(true);
    setError(null);
    
    try {
      // FIXED: Use consistent structure for all write operations
      const requestData = {
        changes: [{
          operation: "create",
          data: newData
        }]
      };
      
      const response = await api.post(`${apiEndpoint}/${tableName}`, requestData);
      
      // FIX: Extract the created record from the correct nested backend response structure
      // Backend returns: {message, results: {success, results: [operations]}}
      const backendResult = response.data.results?.results?.[0];
      if (!backendResult || !backendResult.success) {
        throw new Error('Backend creation failed');
      }
      
      // FIX: Construct the created record object with the correct primary key
      const createdRecord = {
        ...backendResult.data, // The enriched data from backend
        [primaryKey]: backendResult.id // Set the primary key field with the actual ID
      };
      
      // ✅ MANTENUTA LA SOLUZIONE PER LE NOTIFICHE
      setSuccess({ id: Date.now(), message: 'Record creato con successo.' });
      
      // Dopo l'inserimento, naviga all'ultima pagina dove si trova il nuovo record
      const newTotal = (tableInfo.totalRecords || 0) + 1;
      const limit = pagination.limit || 10;
      const lastPageOffset = Math.max(0, Math.floor((newTotal - 1) / limit) * limit);
      await fetchRecords({ offset: lastPageOffset });
      
      return createdRecord; // Return the properly constructed record with the ID
    } catch (err) {
      const formattedError = formatErrorMessage(err);
      setError(formattedError);
      return null;
    } finally {
      setLoading(false);
      setIsProcessingCreate(false);
    }
  }, [apiEndpoint, tableName, primaryKey, fetchRecords, tableInfo.totalRecords, pagination.limit]);

  const updateRecord = useCallback(async (updatedData) => {
    setLoading(true);
    setIsProcessingUpdate(true);
    setError(null);
    setConflictData(null);
    
    try {
      // FIXED: Use the same endpoint and structure as create/delete operations
      const requestData = {
        changes: [{
          operation: "update",
          data: updatedData
        }]
      };
      
      const response = await api.post(`${apiEndpoint}/${tableName}`, requestData);
      
      // Check if the response indicates success
      const backendResult = response.data.results?.results?.[0];
      if (!backendResult || !backendResult.success) {
        // Check for version conflict
        if (response.status === 409 || backendResult?.conflict) {
          setConflictData(response.data);
          return false;
        }
        throw new Error('Backend update failed');
      }
      
      // ✅ MANTENUTA LA SOLUZIONE PER LE NOTIFICHE
      setSuccess({ id: Date.now(), message: 'Record aggiornato con successo' });
      // Optionally refresh data after update
      await fetchRecords();
      return true;
    } catch (err) {
      if (err.response?.status === 409) {
        setConflictData(err.response.data);
        return false;
      }
      
      const formattedError = formatErrorMessage(err);
      setError(formattedError);
      return false;
    } finally {
      setLoading(false);
      setIsProcessingUpdate(false);
    }
  }, [apiEndpoint, tableName, primaryKey, fetchRecords]);

  const deleteRecord = useCallback(async (recordId) => {
    setLoading(true);
    setIsProcessingDelete(true);
    setError(null);
    
    try {
      // FIXED: Use the same endpoint and structure as create/update operations
      const requestData = {
        changes: [{
          operation: "delete",
          data: {
            [primaryKey]: recordId
          }
        }]
      };
      
      const response = await api.post(`${apiEndpoint}/${tableName}`, requestData);
      
      // Check if the response indicates success
      const backendResult = response.data.results?.results?.[0];
      if (!backendResult || !backendResult.success) {
        throw new Error('Backend delete failed');
      }
      
      // ✅ MANTENUTA LA SOLUZIONE PER LE NOTIFICHE
      setSuccess({ id: Date.now(), message: 'Record eliminato con successo' });
      // Optionally refresh data after deletion
      await fetchRecords();
      return true;
    } catch (err) {
      const formattedError = formatErrorMessage(err);
      setError(formattedError);
      return false;
    } finally {
      setLoading(false);
      setIsProcessingDelete(false);
    }
  }, [apiEndpoint, tableName, primaryKey, fetchRecords]);

  // --- Context Value ---
  const contextValue = useMemo(() => ({
    // Data and state
    data,
    loading,
    error,
    success,
    tableInfo,
    
    // Processing states
    isProcessingCreate,
    isProcessingUpdate,
    isProcessingDelete,
    
    // User interactions
    filters,
    pagination,
    sortBy,
    selectedRow,
    conflictData,
    lookupOptions,
    
    // State setters
    setError,
    setSuccess,
    setFilters,
    setPagination,
    setSortBy,
    setSelectedRow,
    setConflictData,
    setLookupOptions,
    
    // API operations
    fetchRecords,
    searchRecords,
    createRecord,
    updateRecord,
    deleteRecord,
    
    // Meta information
    tableName,
    primaryKey,
  }), [
    data, loading, error, success, tableInfo,
    isProcessingCreate, isProcessingUpdate, isProcessingDelete,
    filters, pagination, sortBy, selectedRow, conflictData, lookupOptions,
    setError, setSuccess, setFilters, setPagination, setSortBy, 
    setSelectedRow, setConflictData, setLookupOptions,
    fetchRecords, searchRecords, createRecord, updateRecord, deleteRecord,
    tableName, primaryKey
  ]); // Removed individual setters from dependencies as they are stable

  return (
    <TableContext.Provider value={contextValue}>
      {children}
    </TableContext.Provider>
  );
};
